import { useMemo, useState } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { differenceWith } from "lodash";
import { GripVertical, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  useComboboxAnchor,
} from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { REQUIRED_MARK } from "@/components/BaseForm/fields/fieldClasses";

const SortableStatusRow = ({ name, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: name });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="tw:flex tw:items-center tw:gap-2 tw:rounded-md tw:border tw:border-(--color-border) tw:bg-(--color-surface-overlay) tw:px-2 tw:py-1.5 tw:text-(--color-text-primary)"
    >
      <button
        type="button"
        aria-label="Drag to reorder"
        className="tw:cursor-grab tw:border-none tw:bg-transparent tw:p-1 tw:text-(--color-text-muted) tw:hover:text-foreground tw:active:cursor-grabbing"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="tw:size-4" />
      </button>
      <span className="tw:flex-1">{name}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label="Remove"
        onClick={() => onDelete(name)}
      >
        <Trash2 />
      </Button>
    </div>
  );
};

const CampaignStatusSelector = ({
  campaignStatuses,
  setCampaignStatuses,
  allCampaignStatuses,
}) => {
  const [selection, setSelection] = useState(null);
  const anchorRef = useComboboxAnchor();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    // Without the sortable coordinate getter, arrow keys translate the node by
    // a flat 25px — less than a row, so the drag never crosses into the next
    // index and keyboard reordering silently does nothing.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const options = useMemo(
    () =>
      differenceWith(
        allCampaignStatuses,
        campaignStatuses,
        (a, b) => a.id === b.id
      ).map(({ id, name }) => ({ value: name, label: name, id })),
    [allCampaignStatuses, campaignStatuses]
  );

  const handleAdd = () => {
    if (!selection) {
      return;
    }
    const exists = campaignStatuses.some(item => item.name === selection.value);
    if (!exists) {
      setCampaignStatuses([
        ...campaignStatuses,
        { id: selection.id, name: selection.value },
      ]);
    }
    setSelection(null);
  };

  const handleDelete = name => {
    setCampaignStatuses(campaignStatuses.filter(item => item.name !== name));
  };

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) {
      return;
    }
    const oldIndex = campaignStatuses.findIndex(
      item => item.name === active.id
    );
    const newIndex = campaignStatuses.findIndex(item => item.name === over.id);
    if (oldIndex < 0 || newIndex < 0) {
      return;
    }
    setCampaignStatuses(arrayMove(campaignStatuses, oldIndex, newIndex));
  };

  return (
    <div className="tw:flex tw:flex-col tw:gap-3">
      <div className="tw:flex tw:flex-col tw:gap-1">
        <Label className={REQUIRED_MARK}>Campaign Statuses</Label>
        <p className="tw:text-xs tw:text-(--color-text-muted)">
          Drag to set the order these statuses appear in when a campaign status
          is changed.
        </p>
        <div className="tw:flex tw:items-end tw:gap-2">
          <Combobox
            items={options}
            value={selection}
            onValueChange={setSelection}
            itemToStringLabel={item => item?.label ?? ""}
          >
            <div ref={anchorRef} className="tw:flex-1">
              <ComboboxInput
                placeholder="Select…"
                showTrigger={false}
                className="tw:w-full"
              />
            </div>
            <ComboboxContent anchor={anchorRef}>
              <ComboboxEmpty>No matches</ComboboxEmpty>
              <ComboboxList>
                {item => (
                  <ComboboxItem key={item.value} value={item}>
                    {item.label}
                  </ComboboxItem>
                )}
              </ComboboxList>
            </ComboboxContent>
          </Combobox>
          <Button
            type="button"
            variant="outline"
            onClick={handleAdd}
            disabled={!selection}
          >
            <Plus data-icon="inline-start" />
            Add
          </Button>
        </div>
      </div>
      {campaignStatuses.length === 0 ? (
        <p className="tw:py-2 tw:text-center tw:text-sm tw:text-(--color-text-muted) tw:italic">
          No Campaign Statuses included in this preset
        </p>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={campaignStatuses.map(({ name }) => name)}
            strategy={verticalListSortingStrategy}
          >
            <div className="tw:flex tw:flex-col tw:gap-1">
              {campaignStatuses.map(({ name }) => (
                <SortableStatusRow
                  key={name}
                  name={name}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

export default CampaignStatusSelector;
